/**
 * Nucleo "Persone": livelli, profilo di lavoro, costi e assenze.
 * Le funzioni pure (calendario, capacità, costo orario) servono all'API e al web.
 */
import { z } from "zod";
import { isoDay, optionalText, uuid } from "./common.js";

export const EMPLOYMENT_TYPES = ["employee", "contractor"] as const;
export const EMPLOYMENT_LABEL: Record<(typeof EMPLOYMENT_TYPES)[number], string> = { employee: "Dipendente", contractor: "Consulente esterno" };
export const COST_UNITS = ["hour", "day"] as const;
export const ABSENCE_KINDS = ["vacation", "leave", "training", "other"] as const;
export const ABSENCE_LABEL: Record<(typeof ABSENCE_KINDS)[number], string> = { vacation: "Ferie", leave: "Permesso", training: "Formazione", other: "Altro" };

export const staffLevelSchema = z.object({ name: z.string().trim().min(1).max(60), position: z.number().int().min(0).max(100).default(0) });
export const memberProfileSchema = z.object({
  userId: uuid,
  levelId: uuid.nullable(),
  employment: z.enum(EMPLOYMENT_TYPES),
  jobTitle: optionalText(120),
  weeklyMinutes: z.number().int().min(0).max(80 * 60),
  workDays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
});
export const memberCostRateSchema = z.object({
  userId: uuid,
  validFrom: isoDay,
  costCents: z.number().int().min(0).max(10_000_000),
  unit: z.enum(COST_UNITS),
  note: optionalText(200),
});
export const memberAbsenceSchema = z
  .object({
    userId: uuid,
    startDate: isoDay,
    endDate: isoDay,
    minutesPerDay: z.number().int().min(1).max(24 * 60).nullable(),
    kind: z.enum(ABSENCE_KINDS),
    note: optionalText(200),
  })
  .refine((v) => v.endDate >= v.startDate, { message: "La fine precede l'inizio", path: ["endDate"] });

export type MemberProfileInput = z.infer<typeof memberProfileSchema>;
export type MemberCostRateInput = z.infer<typeof memberCostRateSchema>;
export type MemberAbsenceInput = z.infer<typeof memberAbsenceSchema>;

// ── calendario ────────────────────────────────────────────────────────
const pad = (n: number) => String(n).padStart(2, "0");
export const isoOf = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
export const dateOf = (s: string) => new Date(`${s}T00:00:00.000Z`);
export function addDaysIso(s: string, n: number) {
  const d = dateOf(s);
  d.setUTCDate(d.getUTCDate() + n);
  return isoOf(d);
}
/** Giorno ISO della settimana (1 = lunedì … 7 = domenica). */
export const isoWeekday = (s: string) => ((dateOf(s).getUTCDay() + 6) % 7) + 1;
/** Lunedì della settimana che contiene la data. */
export const mondayOf = (s: string) => addDaysIso(s, 1 - isoWeekday(s));
export function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 3700; d = addDaysIso(d, 1)) out.push(d);
  return out;
}

/** Pasqua (algoritmo di Meeus/Jones/Butcher). */
function easter(year: number) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Festività nazionali italiane (più l'eventuale patrono, "MM-GG"). */
export function italianHolidays(year: number, patron?: string | null): Set<string> {
  const fixed = ["01-01", "01-06", "04-25", "05-01", "06-02", "08-15", "11-01", "12-08", "12-25", "12-26"];
  if (patron && /^\d{2}-\d{2}$/.test(patron)) fixed.push(patron);
  const set = new Set(fixed.map((md) => `${year}-${md}`));
  set.add(addDaysIso(easter(year), 1)); // lunedì dell'Angelo
  return set;
}

export interface WorkProfile {
  weeklyMinutes: number;
  workDays: number[];
}
export const DEFAULT_PROFILE: WorkProfile = { weeklyMinutes: 2400, workDays: [1, 2, 3, 4, 5] };

/** Minuti di lavoro in una giornata lavorativa del profilo. */
export const dailyMinutes = (p: WorkProfile) => (p.workDays.length ? Math.round(p.weeklyMinutes / p.workDays.length) : 0);

export interface AbsenceSpan {
  startDate: string;
  endDate: string;
  minutesPerDay: number | null;
}

/** Capacità giorno per giorno: orario del profilo meno festività e assenze. */
export function capacityByDay(profile: WorkProfile, absences: AbsenceSpan[], from: string, to: string, patron?: string | null): Map<string, number> {
  const out = new Map<string, number>();
  const holidays = new Map<number, Set<string>>();
  const daily = dailyMinutes(profile);
  for (const d of eachDay(from, to)) {
    const y = Number(d.slice(0, 4));
    if (!holidays.has(y)) holidays.set(y, italianHolidays(y, patron));
    let m = profile.workDays.includes(isoWeekday(d)) && !holidays.get(y)!.has(d) ? daily : 0;
    for (const a of absences) if (m > 0 && a.startDate <= d && d <= a.endDate) m = a.minutesPerDay == null ? 0 : Math.max(0, m - a.minutesPerDay);
    out.set(d, m);
  }
  return out;
}

export function capacityMinutes(profile: WorkProfile, absences: AbsenceSpan[], from: string, to: string, patron?: string | null) {
  let s = 0;
  for (const m of capacityByDay(profile, absences, from, to, patron).values()) s += m;
  return s;
}

export interface CostRateRow {
  validFrom: string;
  costCents: number;
  unit: "hour" | "day" | string;
}
/** Costo orario in centesimi valido in una data (giornaliero diviso le ore del giorno del profilo). */
export function hourlyCostAt(rates: CostRateRow[], date: string, profile: WorkProfile = DEFAULT_PROFILE): number | null {
  let best: CostRateRow | null = null;
  for (const r of rates) if (r.validFrom <= date && (!best || r.validFrom > best.validFrom)) best = r;
  if (!best) return null;
  if (best.unit === "day") {
    const hours = dailyMinutes(profile) / 60 || 8;
    return Math.round(best.costCents / hours);
  }
  return best.costCents;
}

/** Saturazione in percentuale (null se la capacità è zero). */
export const saturation = (minutes: number, capacity: number) => (capacity > 0 ? Math.round((minutes / capacity) * 100) : null);
